/**
 * ConnectionStatus
 *
 * Small indicator showing Socket.IO connection status for real-time updates.
 * Used in the dispatcher dashboard header to show live/offline status.
 *
 * Visual States:
 * - Connected:    🟢 Live
 * - Disconnected: 🔴 Offline (click to reconnect)
 * - Reconnecting: 🟡 Reconnecting...
 */

import React from 'react';
import { Box, Tooltip, Typography, CircularProgress } from '@mui/material';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import styles from './ConnectionStatus.module.css';
import classNames from 'classnames';

export interface ConnectionStatusProps {
  /** Whether the socket is connected */
  isConnected: boolean;
  /** Whether we're attempting to reconnect */
  isReconnecting?: boolean;
  /** Error message if connection failed */
  error?: Error | null;
  /** Callback to manually trigger reconnection */
  onReconnect?: () => void;
  /** Whether to show text label (default: true) */
  showLabel?: boolean;
  /** Compact mode - icon only (default: false) */
  compact?: boolean;
}

const ConnectionStatus: React.FC<ConnectionStatusProps> = ({
  isConnected,
  isReconnecting = false,
  error,
  onReconnect,
  showLabel = true,
  compact = false,
}) => {
  const getTooltipContent = () => {
    if (isReconnecting) {
      return 'Reconnecting to real-time updates...';
    }
    if (isConnected) {
      return 'Connected - receiving real-time updates';
    }
    if (error) {
      return `Disconnected: ${error.message}. Click to reconnect.`;
    }
    return 'Disconnected from real-time updates. Click to reconnect.';
  };

  const handleClick = () => {
    if (!isConnected && onReconnect) {
      onReconnect();
    }
  };

  const renderIcon = () => {
    if (isReconnecting) {
      return <CircularProgress size={14} className={styles.spinner} />;
    }
    if (isConnected) {
      return <WifiIcon className={styles.icon} />;
    }
    return <WifiOffIcon className={styles.icon} />;
  };

  const renderLabel = () => {
    if (isReconnecting) {
      return 'Reconnecting...';
    }
    if (isConnected) {
      return 'Live';
    }
    return 'Offline';
  };

  return (
    <Tooltip title={getTooltipContent()} arrow>
      <Box
        className={classNames(styles.container, {
          [styles.connected]: isConnected,
          [styles.disconnected]: !isConnected && !isReconnecting,
          [styles.reconnecting]: isReconnecting,
          [styles.clickable]: !isConnected && onReconnect,
          [styles.compact]: compact,
        })}
        onClick={handleClick}
        role={!isConnected && onReconnect ? 'button' : undefined}
        tabIndex={!isConnected && onReconnect ? 0 : undefined}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick();
          }
        }}
      >
        <Box className={styles.indicator}>{renderIcon()}</Box>
        {showLabel && !compact && (
          <Typography variant="caption" className={styles.label}>
            {renderLabel()}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default ConnectionStatus;
